<style lang="less">
@import "./login.less";
@import "./animate.min.css";

.external-login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.external-login-box {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 40px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 500px;
  width: 90%;
}

.external-login-title {
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
  font-weight: 600;
}

.external-login-loading {
  margin: 20px 0;
}

.external-login-error {
  color: #ff4757;
  font-size: 16px;
  margin: 20px 0;
  padding: 15px;
  background: rgba(255, 71, 87, 0.1);
  border-radius: 8px;
  border-left: 4px solid #ff4757;
}

.external-login-success {
  color: #2ed573;
  font-size: 16px;
  margin: 20px 0;
  padding: 15px;
  background: rgba(46, 213, 115, 0.1);
  border-radius: 8px;
  border-left: 4px solid #2ed573;
}

.external-login-info {
  color: #666;
  font-size: 14px;
  margin: 10px 0;
}
</style>

<template>
  <div class="external-login-container">
    <div class="external-login-box">
      <h1 class="external-login-title">{{ langMap.externalLogin[lang] }}</h1>

      <!-- 加载状态 -->
      <div v-if="loading" class="external-login-loading">
        <Spin size="large">
          <Icon type="ios-loading" size="18" class="spin-icon-load"></Icon>
          <div>{{ langMap.verifyingAccess[lang] }}</div>
        </Spin>
      </div>

      <!-- 错误信息 -->
      <div v-if="errorMessage" class="external-login-error">
        <Icon type="ios-close-circle" size="20" style="margin-right: 8px;"></Icon>
        {{ errorMessage }}
      </div>

      <!-- 成功信息 -->
      <div v-if="successMessage" class="external-login-success">
        <Icon type="ios-checkmark-circle" size="20" style="margin-right: 8px;"></Icon>
        {{ successMessage }}
      </div>

      <!-- 租户信息显示 -->
      <div v-if="tenantInfo && !errorMessage" class="external-login-info">
        <p><strong>{{ langMap.tenantName[lang] }}:</strong> {{ tenantInfo.name }}</p>
        <p v-if="tenantInfo.description"><strong>{{ langMap.description[lang] }}:</strong> {{ tenantInfo.description }}</p>
      </div>
    </div>

    <!-- 备案信息组件 -->
    <FilingInfo />
  </div>
</template>

<script>
import { getTenantInfoById } from "@/api/tenant";
import { setTenantId, setTenantInfo, setExternalAccess ,removeToken } from "@/libs/util";
import langMap from '@/utils/language';
import { mapState } from 'vuex';

export default {
  name: 'ExternalLogin',
  components: {
    FilingInfo: () => import('@/components/filing-info/index.vue')
  },
  data() {
    return {
      langMap: langMap,
      loading: false,
      errorMessage: '',
      successMessage: '',
      tenantInfo: null,
      token: '',
      tenantId: '',
      thdime: '', // 存储地址栏参数 thdime
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    })
  },
  created() {
    document.title = '第三方接入登录 - 机器人管控平台';
    removeToken()
    this.initExternalLogin();
  },
  methods: {
    // 初始化外部登录
    initExternalLogin() {
      // 获取URL参数 - 支持hash路由和query参数
      let urlParams;
      console.log(window.location.hash)
      if (window.location.hash.includes('?')) {
        // 从hash中获取参数
        urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
      } else {
        // 从search中获取参数
        urlParams = new URLSearchParams(window.location.search);
      }

      // 也可以从Vue Router的query中获取
      if (this.$route.query.token) {
        this.token = this.$route.query.token;
        this.tenantId = this.$route.query.tenantId;
        this.thdime = this.$route.query.thdime;
      } else {
        this.token = urlParams.get('token');
        this.tenantId = urlParams.get('tenantId');
        this.thdime = urlParams.get('thdime');
      }

      // 存储 thdime 参数到 sessionStorage
      if (this.thdime) {
        sessionStorage.setItem('thdime', this.thdime);
      }

      // 验证必要参数
      if (!this.token) {
        this.errorMessage = langMap.tokenRequired[this.lang] || 'Token参数缺失';
        return;
      }

      if (!this.tenantId) {
        this.errorMessage = langMap.tenantIdRequired[this.lang] || '租户ID参数缺失';
        return;
      }

      // 开始验证流程
      this.verifyAccess();
    },

    // 验证访问权限
    async verifyAccess() {
      this.loading = true;
      this.errorMessage = '';
      // 设置外部接入状态
      setExternalAccess(true);
      try {
        // 设置token
        this.$store.commit('setToken', this.token);

        // 获取租户信息
        const tenantRes = await getTenantInfoById(this.tenantId);

        if (tenantRes.state === 0) {
          removeToken()
          this.errorMessage = tenantRes.msg || langMap.tenantNotFound[this.lang] || '租户信息获取失败';
          this.loading = false;
          return;
        }

        // 存储租户信息
        this.tenantInfo = tenantRes.data;
        setTenantId(this.tenantId);
        setTenantInfo(tenantRes.data);

        // 显示成功信息
        this.successMessage = langMap.accessVerified[this.lang] || '访问验证成功，正在跳转...';

        // 延迟跳转到首页
        setTimeout(() => {
          this.$router.push({
            name: 'home'
          });
        }, 1500);

      } catch (error) {
        removeToken()
        console.error('External login error:', error);
        this.errorMessage = langMap.verificationFailed[this.lang] || '验证失败，请联系管理员';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
